import { useCallback, useMemo, useState } from 'react';
import type { LinkedWorkItem, LinkedWorkItemsQueryParams } from 'types/linkedWorkItem';
import { linkedWorkItemsApi } from './linkedWorkItems.api';

/**
 * Seconds a native page may age before it is fetched again when a picker mounts, changes scope,
 * or (via `refresh`) opens. Native items can be created or edited through the public API while a
 * page is open, and a native list is a cheap indexed query, so a picker re-asks once the answer it
 * holds is older than this rather than serving the first load for the whole session.
 */
export const NATIVE_LINKED_WORK_REFRESH_SECONDS = 30;

export type NativeLinkedWorkScopeArgs = Omit<LinkedWorkItemsQueryParams, 'limit' | 'offset'>;

export interface NativeLinkedWorkBrowser {
  /** Every loaded page of the current scope, in server order, deduplicated by id. */
  items: Array<LinkedWorkItem>;
  isLoading: boolean;
  isFetching: boolean;
  /** A page of this browser has settled at least once (with data or an error). */
  hasFetched: boolean;
  /** The server reports rows beyond the loaded pages, and nothing is in flight. */
  hasMore: boolean;
  loadMore: () => void;
  /** Re-fetch the list if what it holds is older than `NATIVE_LINKED_WORK_REFRESH_SECONDS`. */
  refresh: () => void;
  error: unknown;
}

interface UseNativeLinkedWorkBrowserParams {
  scopeArgs: NativeLinkedWorkScopeArgs;
  pageSize: number;
  skip: boolean;
}

/**
 * Pages the native (Rails) linked-work list with `offset`, the native counterpart of
 * `useScopedTaskBrowser`. Pages accumulate per scope (search, status, project) and reset when the
 * scope changes. `X-Total-Count` says whether more rows exist.
 *
 * Only the page being fetched is subscribed; pages already scrolled past are kept as they were
 * when the next one was requested. Every page still flows through the endpoint's
 * `onQueryStarted`, so each one keeps feeding the shared task pool that labels existing links.
 */
export const useNativeLinkedWorkBrowser = ({
  scopeArgs,
  pageSize,
  skip,
}: UseNativeLinkedWorkBrowserParams): NativeLinkedWorkBrowser => {
  const { search, status, projectId, assignedUserId } = scopeArgs;
  const scopeKey = JSON.stringify([
    search ?? null,
    status ?? null,
    projectId ?? null,
    assignedUserId ?? null,
    pageSize,
  ]);

  const [activeKey, setActiveKey] = useState(scopeKey);
  const [pageCount, setPageCount] = useState(1);
  // Pages before the one currently subscribed, captured as each next page is requested.
  const [earlierPages, setEarlierPages] = useState<Array<Array<LinkedWorkItem>>>([]);

  // Reset during render so the query never runs once with the new scope and the old offset, and a
  // render never lists the previous scope's pages beside the new one.
  if (scopeKey !== activeKey) {
    setActiveKey(scopeKey);
    setPageCount(1);
    setEarlierPages([]);
  }
  const isActiveScope = scopeKey === activeKey;
  const pageIndex = isActiveScope ? pageCount - 1 : 0;

  const query = linkedWorkItemsApi.endpoints.getLinkedWorkItems.useQuery(
    {
      ...(search != null ? { search } : {}),
      ...(status != null ? { status } : {}),
      ...(projectId != null ? { projectId } : {}),
      ...(assignedUserId != null ? { assignedUserId } : {}),
      limit: pageSize,
      ...(pageIndex > 0 ? { offset: pageIndex * pageSize } : {}),
    },
    { skip, refetchOnMountOrArgChange: NATIVE_LINKED_WORK_REFRESH_SECONDS }
  );

  // `currentData`, not `data`: `data` keeps the previous args' page while new args are fetching —
  // and after that fetch fails — so another scope's rows would be listed as this one's.
  const currentPage = skip ? undefined : query.currentData;
  const currentItems = currentPage?.items;
  const totalCount = currentPage?.totalCount ?? null;

  const items = useMemo(() => {
    if (skip) return [];
    const pages = isActiveScope ? earlierPages.slice(0, pageIndex) : [];
    const seen = new Set<number>();
    const merged: Array<LinkedWorkItem> = [];
    // Offsets shift when rows are created or removed between page loads, so the same row can
    // arrive on two pages.
    for (const item of [...pages.flat(), ...(currentItems ?? [])]) {
      if (seen.has(item.id)) continue;
      seen.add(item.id);
      merged.push(item);
    }
    return merged;
  }, [skip, isActiveScope, earlierPages, pageIndex, currentItems]);

  const loadedThroughCurrentPage = pageIndex * pageSize + (currentItems?.length ?? 0);
  const hasMore =
    !skip &&
    isActiveScope &&
    !query.isFetching &&
    query.error == null &&
    currentItems != null &&
    currentItems.length > 0 &&
    (totalCount != null ? loadedThroughCurrentPage < totalCount : currentItems.length >= pageSize);

  const loadMore = useCallback(() => {
    if (!hasMore || currentItems == null) return;
    setEarlierPages(prev => [...prev.slice(0, pageIndex), currentItems]);
    setPageCount(pageIndex + 2);
  }, [hasMore, currentItems, pageIndex]);

  const { fulfilledTimeStamp, isUninitialized, refetch } = query;
  const refresh = useCallback(() => {
    if (skip || isUninitialized || fulfilledTimeStamp == null) return;
    if (Date.now() - fulfilledTimeStamp < NATIVE_LINKED_WORK_REFRESH_SECONDS * 1000) return;
    if (pageIndex > 0) {
      // New and changed rows land on the first page (the list is most recently updated first),
      // so a stale list restarts there. Moving back to offset 0 is an arg change, which refetches
      // it past the threshold.
      setEarlierPages([]);
      setPageCount(1);
      return;
    }
    refetch();
  }, [skip, isUninitialized, fulfilledTimeStamp, pageIndex, refetch]);

  return {
    items,
    isLoading: !skip && query.isLoading,
    isFetching: !skip && query.isFetching,
    hasFetched: query.data != null || query.error != null,
    hasMore,
    loadMore,
    refresh,
    error: skip ? undefined : query.error,
  };
};
